import { computed } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { KanbanTimeOff } from './interfaces'
import type { BadgeTimeOffMode } from '@shared/ui/badges'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useProfileStore } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'

import { KanbanTimeOffStatus } from './enums'

const TAR_ON_TRACK_THRESHOLD = 85

interface TimeOffItemOptions {
  timeOff: Ref<KanbanTimeOff>
}

interface TimeOffItemReturn {
  refIsUpcoming: ComputedRef<boolean>
  refStatusText: ComputedRef<string>
  refStatusMode: ComputedRef<BadgeTimeOffMode>
  refAbsenceRange: ComputedRef<string>
  refTimezoneLabel: ComputedRef<string>
  refTarText: ComputedRef<string>
  refTarStatusText: ComputedRef<string>
  refIsTarOnTrack: ComputedRef<boolean>
  refCreatedAt: ComputedRef<string>
}

export const useTimeOffItem = ({ timeOff }: TimeOffItemOptions): TimeOffItemReturn => {
  const profileStore = useProfileStore()
  const dayjs = useDayjs()

  const refIsUpcoming = computed<boolean>(() => timeOff.value.status === KanbanTimeOffStatus.Upcoming)

  const refStatusText = computed<string>(() => refIsUpcoming.value ? 'Upcoming' : 'Completed')

  const refStatusMode = computed<BadgeTimeOffMode>(() => refIsUpcoming.value ? 'info' : 'success')

  const refAbsenceRange = computed<string>(() => {
    const start = dayjs(timeOff.value.from)
    const end = dayjs(timeOff.value.to)

    return `${start.format(DateFormat.FileDate)} from ${start.format(DateFormat.DefaultTime)} - ${end.format(DateFormat.FileDate)} to ${end.format(DateFormat.DefaultTime)}`
  })

  const refTimezoneLabel = computed<string>(() => {
    const tz = profileStore.getTimezone

    return tz ? `Shown in your time zone (${tz})` : ''
  })

  const refTarText = computed<string>(() => `${timeOff.value.predicted_attendance_rate}/100`)

  const refIsTarOnTrack = computed<boolean>(() => timeOff.value.predicted_attendance_rate >= TAR_ON_TRACK_THRESHOLD)

  const refTarStatusText = computed<string>(() => refIsTarOnTrack.value ? '(on track)' : '(below target)')

  const refCreatedAt = computed<string>(() => `Created on ${dayjs(timeOff.value.created_at).format(DateFormat.KanbanDate)}`)

  return {
    refIsUpcoming,
    refStatusText,
    refStatusMode,
    refAbsenceRange,
    refTimezoneLabel,
    refTarText,
    refTarStatusText,
    refIsTarOnTrack,
    refCreatedAt,
  }
}
